import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, Hourglass, BookOpen, Coins, Shield, FileText, Heart, MapPin, Gavel, ScrollText, ShieldCheck, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import BookReader from "@/components/site/BookReader";
import { BIBLIOTHEK, LEITFAEDEN } from "@/data/leitfaeden";

// Reihenfolge der Buchregal-Bücher (nur verfügbare Guides, prominenteste zuerst)
const BOOKSHELF_SLUGS = [
  "wer-darf-was-atlas",
  "abrechnungs-atlas",
  "md-pruefung-7-tage",
  "mdk-survival-guide",
  "pflegegrad-spielbuch",
  "haftungsfallen-pdl",
  "kuerzungen-widerspruch",
  "demenz-25-saetze",
  "sturz-bewohner",
  "leistungsgruppen-lg1-lg2",
];

// (BOOK_THEMES entfernt — jetzt Cover-Image-basierter Editorial-Grid)

export default function Leitfaeden() {
  const [openSlug, setOpenSlug] = useState(null);
  const openGuide = openSlug ? LEITFAEDEN[openSlug] : null;

  useEffect(() => {
    document.title = "Kostenlose Leitfäden für Pflegekräfte & PDLs · Helden-Bibliothek | Dickies-Campus";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = "Praxis-Leitfäden für den Pflegealltag: Sturz, Demenz-Kommunikation, MD-Prüfung, Angehörigen-Beschwerden u. v. m. – kostenlos als PDF von Dickies-Campus.";
    window.scrollTo(0, 0);
  }, []);

  const available = new Set(BIBLIOTHEK.flatMap((c) => c.items).filter((i) => i.href).map((i) => i.href)).size;

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* HERO */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra" data-testid="bibliothek-kicker">
                Kostenlos · PDF · Aus der Praxis
              </span>
            </div>
            <h1 className="font-display text-[2.6rem] sm:text-5xl lg:text-[3.6rem] text-sand-800 tracking-tight leading-[1.04] max-w-3xl"
                data-testid="bibliothek-h1">
              Die Helden-Bibliothek. <br />
              <span className="text-terra italic">Leitfäden, die Dienste retten.</span>
            </h1>
            <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.65] max-w-2xl">
              Konkrete Probleme, konkrete Lösungen: Jeder Leitfaden löst genau eine Situation
              aus deinem Pflegealltag – mit Checklisten, Formulierungen und Vorlagen.
              <b> {available} Leitfäden sind bereits kostenlos verfügbar</b>, die Bibliothek wächst monatlich.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FLAGSHIP · Wer darf was Atlas (200+ Seiten · das Fundament) */}
      <section data-testid="bibliothek-atlas-flagship" className="relative bg-peach-50 text-sand-800 overflow-hidden border-t border-cream-300">
        {/* dekorative Schichten · weich + rosa */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-rose/25 blur-3xl" />
          <div className="absolute -bottom-40 -right-32 w-[32rem] h-[32rem] rounded-full bg-peach-200/40 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 lg:py-32">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-20 items-center">
            {/* LEFT · Headline + Story */}
            <Reveal>
              <div>
                <div className="flex items-center gap-3 mb-7">
                  <span className="h-px w-14 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.32em] font-bold text-terra">
                    Flagship · Das Fundament unserer Bibliothek
                  </span>
                </div>

                <h2 className="font-display text-[2.6rem] sm:text-5xl lg:text-[3.8rem] tracking-tight leading-[1.02] text-sand-800">
                  Wer darf was<br />
                  im Pflegedienst?<br />
                  <span className="text-terra italic">Über 200 Seiten Klarheit.</span>
                </h2>

                <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.7] max-w-xl">
                  Das größte und wichtigste Werk, das wir je gebaut haben.
                  Acht Monate Arbeit, 16 Landes-Rahmenverträge ausgewertet,
                  42 Behandlungspflege-Tätigkeiten gegen den MD-Prüfraster gehalten.
                  Mit dem <b className="text-sand-800">4-Label-System</b>{" "}
                  (Gesetz · Landesvertrag · MD-QM · Praxistipp) bekommst du in drei Sekunden
                  Blickzeit die rechtssichere Antwort &#8211; für dein Bundesland.
                </p>

                <p className="mt-5 text-sand-600 text-[14.5px] leading-[1.7] max-w-xl">
                  Geschrieben aus reiner Pflege-Perspektive. <b className="text-sand-800">Kostenlos.</b> Ohne Bezahlschranke.
                  Wir finanzieren ihn über unsere Weiterbildungen &#8211; weil wir wissen:
                  Wer diesen Atlas einmal gelesen hat, weiß, welche Qualifikation im Team fehlt.
                </p>

                {/* 4-Label-Pills · was drin steckt */}
                <div className="mt-9">
                  <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra mb-3.5">
                    Das 4-Label-Pre-Flight-System
                  </div>
                  <ul className="grid grid-cols-2 gap-2.5 max-w-md">
                    {[
                      { icon: Gavel, k: "Gesetz", desc: "Bundesrecht" },
                      { icon: ScrollText, k: "Landesvertrag", desc: "16 BL geprüft" },
                      { icon: ShieldCheck, k: "MD-QM", desc: "Prüfraster" },
                      { icon: CheckCircle2, k: "Helden-Praxistipp", desc: "Empfehlung" },
                    ].map(({ icon: Icon, k, desc }) => (
                      <li key={k} className="flex items-center gap-3 rounded-2xl bg-white border border-cream-300 px-4 py-3">
                        <Icon className="h-4 w-4 text-terra shrink-0" strokeWidth={1.8} />
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-semibold text-sand-800 leading-tight">{k}</div>
                          <div className="text-[10.5px] uppercase tracking-[0.18em] text-sand-500 mt-0.5">{desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTAs */}
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/leitfaden/wer-darf-was-atlas" data-testid="bibliothek-atlas-cta-primary"
                    className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 sm:px-8 h-13 py-3.5 text-[14.5px] font-semibold transition-colors group">
                    <Download className="h-4 w-4" strokeWidth={2} />
                    Vorschau kostenlos anfordern
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </Link>
                  <Link to="/wer-darf-was" data-testid="bibliothek-atlas-cta-secondary"
                    className="inline-flex items-center gap-2 rounded-full border border-terra/40 text-terra hover:bg-white px-7 h-13 py-3.5 text-[14.5px] font-medium transition-colors">
                    <Sparkles className="h-4 w-4" strokeWidth={1.8} />
                    Was steckt drin? Vorschau
                  </Link>
                </div>

                {/* Drei Formate · Kurz / Praxis / Voll */}
                <div className="mt-8" data-testid="bibliothek-atlas-formate">
                  <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra mb-3.5">
                    Ein Werk · drei Formate
                  </div>
                  <ul className="space-y-2 max-w-md">
                    <li>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/leadmagnet/wer-darf-was-atlas/kurzversion`}
                        target="_blank" rel="noreferrer"
                        data-testid="atlas-download-kurzversion"
                        className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-cream-300 hover:border-terra/50 px-4 py-3 transition-colors group">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-sand-800 leading-tight">Kurzversion · ~20 Seiten</div>
                          <div className="text-[11px] text-sand-500 mt-0.5">Top-10-Tätigkeiten · Sofort-Download, ohne Anmeldung</div>
                        </div>
                        <Download className="h-4 w-4 text-terra shrink-0 transition-transform group-hover:translate-y-0.5" strokeWidth={2} />
                      </a>
                    </li>
                    <li>
                      <Link to="/leitfaden/wer-darf-was-atlas" data-testid="atlas-link-praxisversion"
                        className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-cream-300 hover:border-terra/50 px-4 py-3 transition-colors">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-sand-800 leading-tight">Praxisversion · ~40 Seiten</div>
                          <div className="text-[11px] text-sand-500 mt-0.5">Alle 42 Tätigkeiten + Checklisten · gratis im Campus</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-terra shrink-0" strokeWidth={2} />
                      </Link>
                    </li>
                    <li>
                      <Link to="/shop/wer-darf-was-atlas" data-testid="atlas-link-vollversion"
                        className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-cream-300 hover:border-terra/50 px-4 py-3 transition-colors">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-sand-800 leading-tight">Vollversion · 251 Seiten</div>
                          <div className="text-[11px] text-sand-500 mt-0.5">Matrix, Haftung, Lehrfälle, Mustertexte · Fachbuch-PDF · 49,99 € im Shop</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-terra shrink-0" strokeWidth={2} />
                      </Link>
                    </li>
                  </ul>
                </div>

                <p className="mt-6 text-[12px] text-sand-500 leading-relaxed max-w-md">
                  Vorschau & Praxisversion kostenlos · DSGVO-konform · Sofort als PDF in deinem Postfach.
                  Die 251-Seiten-Vollversion gibt es als Fachbuch-PDF im Shop (49,99 €).
                </p>
              </div>
            </Reveal>

            {/* RIGHT · Stat-Block (das Spec-Sheet eines Werks) */}
            <Reveal delay={80}>
              <div className="relative">
                {/* Editorial Banderole */}
                <div className="absolute -top-4 left-8 z-10 inline-flex items-center gap-2 rounded-full bg-terra text-white px-4 h-8 text-[10.5px] uppercase tracking-[0.24em] font-bold">
                  <Sparkles className="h-3 w-3" strokeWidth={2.2} /> Stand · Juli 2026
                </div>
                <div className="rounded-3xl bg-white text-sand-800 p-8 sm:p-10 border border-cream-300">
                  <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra">
                    Spec-Sheet
                  </div>
                  <h3 className="mt-3 font-display text-2xl sm:text-[1.85rem] text-sand-800 leading-tight">
                    &bdquo;Wer darf was&ldquo;-Atlas
                    <br />
                    <span className="italic text-terra">Der Delegations-Atlas.</span>
                  </h3>

                  <dl className="mt-7 divide-y divide-cream-300">
                    {[
                      ["Seiten", "200+"],
                      ["Tätigkeiten", "42 (A–H)"],
                      ["Kategorien", "8 (Medikamente · Injekt · Komp · Wund · Kath · Ern · Atmung · Spezial)"],
                      ["Bundesländer", "Alle 16 (Steckbrief je Land)"],
                      ["Didaktische Lehrfälle", "7 pro Tätigkeit"],
                      ["Mustertexte", "Eskalation Pflege → Arzt"],
                      ["Bonus", "Wer darf was OHNE Delegation"],
                      ["Lese-Empfehlung", "PDL · QM · Pflegefachkraft"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between gap-4 py-3">
                        <dt className="text-[12px] uppercase tracking-[0.18em] text-sand-500 font-semibold">{k}</dt>
                        <dd className="text-[13.5px] text-sand-800 font-medium text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>

                  <div className="mt-6 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-terra text-white px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                      <Download className="h-3 w-3" strokeWidth={2.2} /> Kostenlos
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-rose/35 text-brand-darkred px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                      Premium · Lead-Magnet
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-peach-100 text-sand-700 px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                      <MapPin className="h-3 w-3" strokeWidth={2.2} /> 16 BL
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BIBLIOTHEK · Editorial Gallery */}
      <section
        className="relative bg-cream-50 py-28 md:py-40 border-t border-cream-300"
        data-testid="bibliothek-bookshelf"
      >
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-terra">
                Ausgabe · 2026 · Nr. 1
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] text-brand-darkred tracking-tight leading-[1.05] max-w-3xl font-normal">
              Die Helden-Edition.
            </h2>
            <p className="mt-5 max-w-2xl text-sand-700 text-[16px] md:text-[17px] leading-[1.7]">
              Zehn Fach-Monografien für Pflegekräfte und PDLs. Klick auf ein Cover,
              blättere durch die Kernaussagen — und fordere die vollständige
              PDF-Ausgabe kostenlos an.
            </p>
          </Reveal>

          <div
            data-testid="bibliothek-bookshelf-row"
            className="mt-16 md:mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 gap-y-12 md:gap-x-10 md:gap-y-16"
          >
            {BOOKSHELF_SLUGS.map((slug, i) => {
              const guide = LEITFAEDEN[slug];
              if (!guide) return null;
              const num = String(i + 1).padStart(2, "0");
              return (
                <motion.button
                  key={slug}
                  onClick={() => setOpenSlug(slug)}
                  data-testid={`bibliothek-book-${slug}`}
                  className="group text-left flex flex-col gap-4"
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                  <div className="relative aspect-[3/4] w-full overflow-hidden bg-peach-50 rounded-sm shadow-[0_10px_40px_-15px_rgba(82,42,37,0.25)] group-hover:shadow-[0_20px_50px_-15px_rgba(82,42,37,0.4)] transition-shadow duration-500">
                    <img
                      src={`/covers/${slug}.png`}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                    />
                    {/* Editorial-Nummer oben links */}
                    <div className="absolute top-4 left-4 font-display text-[13px] text-white/90 tracking-[0.25em] mix-blend-difference">
                      №{num}
                    </div>
                    {/* Hover-Overlay + Pfeil */}
                    <div className="absolute inset-0 bg-brand-darkred/0 group-hover:bg-brand-darkred/10 transition-colors duration-500" />
                    <div className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-cream-50/95 backdrop-blur text-brand-darkred inline-flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 shadow-md">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="flex flex-col min-h-[132px]">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-1.5">
                      {guide.kicker}
                    </div>
                    <h3 className="font-display text-[19px] md:text-[21px] leading-[1.15] tracking-tight text-brand-darkred group-hover:text-terra transition-colors duration-300 line-clamp-2">
                      {guide.h1a}
                      {guide.h1b && <> <span className="italic text-terra">{guide.h1b}</span></>}
                    </h3>
                    <p className="mt-2 text-[13px] text-sand-600 leading-[1.55] line-clamp-2">
                      {guide.intro}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-16 md:mt-20 flex items-center justify-center gap-3 text-[12px] text-sand-500 tracking-[0.18em] uppercase">
            <span className="h-px w-8 bg-sand-300" />
            <span>Blättern mit ← → · Esc schließt</span>
            <span className="h-px w-8 bg-sand-300" />
          </div>
        </div>
      </section>

      {/* PREMIUM-BUNDLES HERO */}
      <section className="bg-cream-50 py-14 md:py-20 border-t border-cream-300" data-testid="bibliothek-bundles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-brand-darkred" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-brand-darkred">
                Premium-Bundles · für Pflegedienste
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-[2.4rem] text-sand-800 tracking-tight leading-[1.1] max-w-3xl">
              Die zwei Verteidigungs-Linien <br />
              <span className="text-terra italic">für ambulante Pflegedienste.</span>
            </h2>
            <p className="mt-5 text-sand-700 text-[15px] sm:text-[16px] leading-[1.7] max-w-2xl">
              Premium-Bundles bündeln je zwei aufeinander abgestimmte Leitfäden in einem Funnel.
              Die Vollversionen liegen gratis im Dickies-Campus – Helden-Basic-Account (0&nbsp;€) reicht.
            </p>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-2 gap-5 md:gap-7">
            <Reveal>
              <Link
                to="/leitfaeden/cash-bundle"
                data-testid="bibliothek-bundle-cash"
                className="group relative flex flex-col h-full rounded-3xl bg-white border border-brand-rose/50 text-sand-800 p-8 md:p-10 overflow-hidden hover:border-terra transition-colors"
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-rose/25 blur-2xl pointer-events-none" />
                <div className="relative flex flex-col h-full">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-4">
                    <Coins className="h-3.5 w-3.5" strokeWidth={2.2} /> Cash-Bundle &middot; Abrechnung
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.85rem] leading-tight text-sand-800">
                    Pflegekasse kürzt? <span className="text-terra italic">Hol dir das Cash zurück.</span>
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-sand-700">
                    <b className="text-sand-800">Abrechnungs-Atlas</b> (alle 16 Bundesländer, Vertragsquellen) +
                    <b className="text-sand-800"> Musterwiderspruch-Guide</b> (12 fertige Texte, Fristen,
                    Eskalation bis Sozialgericht).
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-darkred">
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">2 PDFs</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">16 BL</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">12 Mustertexte</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">2.640 € Hero-Case</span>
                  </div>
                  <div className="mt-auto pt-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-terra text-white px-5 h-10 text-[13.5px] font-semibold group-hover:bg-terra-dark transition-colors">
                      Bundle anfordern <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>

            <Reveal delay={80}>
              <Link
                to="/leitfaeden/md-schutzschild"
                data-testid="bibliothek-bundle-md"
                className="group relative flex flex-col h-full rounded-3xl bg-white border border-terra/40 text-sand-800 p-8 md:p-10 overflow-hidden hover:border-terra transition-colors"
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-peach-200/35 blur-2xl pointer-events-none" />
                <div className="relative flex flex-col h-full">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-terra mb-4">
                    <Shield className="h-3.5 w-3.5" strokeWidth={2.2} /> MD-Schutzschild &middot; Prüfung
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.85rem] leading-tight text-sand-800">
                    MD-Prüfung in 14 Tagen? <span className="text-terra italic">Das Schutzschild für PDLs.</span>
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-sand-700">
                    <b className="text-sand-800">7-Tage-Vorbereitungsplan</b> +
                    <b className="text-sand-800"> MD-Survival-Guide</b> (23 Fragen, 7 No-Gos) +
                    <b className="text-sand-800"> Haftungsfallen 2026</b> (10 didaktische Lehrfälle).
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-terra">
                    <span className="rounded-full bg-peach-100 px-3 py-1">3 PDFs</span>
                    <span className="rounded-full bg-peach-100 px-3 py-1">23 MD-Fragen</span>
                    <span className="rounded-full bg-peach-100 px-3 py-1">10 Haftungs-Fälle</span>
                    <span className="rounded-full bg-peach-100 px-3 py-1">13 → 0 Mängel</span>
                  </div>
                  <div className="mt-auto pt-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-terra text-white px-5 h-10 text-[13.5px] font-semibold group-hover:bg-terra-dark transition-colors">
                      Bundle anfordern <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>

          <p className="mt-7 text-[12.5px] text-sand-500 max-w-2xl">
            Beide Bundles werden mit einer E-Mail freigeschaltet. Die Vollversionen findest du
            danach in der Helden-Schatzkiste &#8211; gratis mit Helden-Basic-Account.
          </p>
        </div>
      </section>

      {/* B2C BUNDLE — Pflegekraft-Bundle */}
      <section className="bg-peach-50 py-14 md:py-20 border-t border-cream-300" data-testid="bibliothek-bundle-b2c">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                Premium-Bundle · für Pflegekräfte &amp; Angehörige
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-[2.4rem] text-sand-800 tracking-tight leading-[1.1] max-w-3xl">
              Für Pflegekräfte. <br />
              <span className="text-terra italic">Für Angehörige.</span>
            </h2>
            <p className="mt-5 text-sand-700 text-[15px] sm:text-[16px] leading-[1.7] max-w-2xl">
              Drei Werkzeuge für den schwierigsten Pflegealltag: Pflegegrad-Streit beim MDK,
              Demenz-Kommunikation im Alltag, Sturz-Akutleitfaden in den ersten 60 Minuten.
            </p>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-2 gap-7 items-start">
            <Reveal>
              <Link
                to="/leitfaeden/pflegekraft-bundle"
                data-testid="bibliothek-bundle-pflegekraft"
                className="group relative flex flex-col h-full rounded-3xl bg-white border border-brand-rose/50 text-sand-800 p-8 md:p-10 overflow-hidden hover:border-terra transition-colors"
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-rose/25 blur-2xl pointer-events-none" />
                <div className="relative flex flex-col h-full">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-4">
                    <Heart className="h-3.5 w-3.5" strokeWidth={2.2} /> Pflegekraft-Bundle
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.85rem] leading-tight text-sand-800">
                    Drei Werkzeuge <span className="text-terra italic">für deine Patient:innen.</span>
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-sand-700">
                    <b className="text-sand-800">Pflegegrad-Spielbuch</b> +
                    <b className="text-sand-800"> Demenz · 25 Sätze</b> +
                    <b className="text-sand-800"> Sturz-Akutleitfaden</b> &#8211; das Geschenk-Paket
                    für Pflegekräfte und Angehörige.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-darkred">
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">3 PDFs</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">6 NBA-Module</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">25 Demenz-Sätze</span>
                    <span className="rounded-full bg-brand-rose/30 px-3 py-1">Sturz-Akut</span>
                  </div>
                  <div className="mt-auto pt-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-terra text-white px-5 h-10 text-[13.5px] font-semibold group-hover:bg-terra-dark transition-colors">
                      Bundle anfordern <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>

            <Reveal delay={80}>
              <div className="rounded-3xl border border-cream-300 bg-white p-8 md:p-10">
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra mb-3">
                  Was du bekommst
                </div>
                <ul className="space-y-3 text-[14.5px] text-sand-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-terra flex-shrink-0" />
                    <span><b className="text-sand-800">Pflegegrad-Spielbuch</b> – die 6 NBA-Module richtig argumentieren, MDK-Termin meistern, Höherstufung holen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-terra flex-shrink-0" />
                    <span><b className="text-sand-800">25 Demenz-Sätze</b> – Validation statt Korrektur, in 5 Kategorien sortiert, sofort einsetzbar.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-terra flex-shrink-0" />
                    <span><b className="text-sand-800">Sturz-Akutleitfaden</b> – Schritt für Schritt durch die ersten 60 Minuten nach dem Sturz, mit Doku-Vorlage.</span>
                  </li>
                </ul>
                <p className="mt-6 text-[12.5px] text-sand-500 leading-relaxed">
                  Mit deiner E-Mail-Bestätigung freigeschaltet. Die Vollversionen findest du
                  danach in der Helden-Schatzkiste – gratis mit Helden-Basic-Account.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BIBLIOTHEK */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {BIBLIOTHEK.map((cat, idx) => (
            <Reveal key={cat.category} delay={idx * 40}>
              <div data-testid={`bibliothek-cat-${idx}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-terra/10 text-terra">
                    <BookOpen className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={1.7} />
                  </span>
                  <h2 className="font-display text-2xl md:text-[1.8rem] text-sand-800 leading-tight">
                    {cat.category}
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {cat.items.map((item) =>
                    item.href ? (
                      <Link
                        key={item.title}
                        to={item.href}
                        data-testid={`bibliothek-item-${item.href.split("/").pop()}`}
                        className="group flex flex-col rounded-2xl bg-white border border-terra/30 p-5 hover:border-terra transition-colors"
                      >
                        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-terra text-white px-3 h-6 text-[10px] uppercase tracking-[0.18em] font-bold">
                          <Download className="h-3 w-3" strokeWidth={2.2} />
                          Kostenlos
                        </span>
                        <h3 className="mt-3 font-semibold text-sand-800 text-[15px] leading-snug">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-[12.5px] text-sand-600 leading-relaxed">
                          {item.tags.join(" · ")}
                        </p>
                        <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-terra text-[13px] font-semibold">
                          Jetzt herunterladen
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                        </span>
                      </Link>
                    ) : (
                      <div
                        key={item.title}
                        className="flex flex-col rounded-2xl bg-cream-100/60 border border-cream-300 p-5"
                      >
                        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-cream-200 text-sand-600 px-3 h-6 text-[10px] uppercase tracking-[0.18em] font-bold">
                          <Hourglass className="h-3 w-3" strokeWidth={2} />
                          Bald verfügbar
                        </span>
                        <h3 className="mt-3 font-semibold text-sand-700 text-[15px] leading-snug">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-[12.5px] text-sand-500 leading-relaxed">
                          {item.tags.join(" · ")}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-rose py-24 md:py-36">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-brand-darkred tracking-tight leading-[1.1]">
              Dein Thema fehlt? <span className="text-white">Sag es uns.</span>
            </h2>
            <p className="mt-5 text-brand-darkred/80 text-[15.5px] leading-[1.7] max-w-xl mx-auto">
              Wir entwickeln die Bibliothek nach euren echten Problemen.
              Schreib uns, welchen Leitfaden du als Nächstes brauchst – oder hol dir
              im Dickies-Campus Zugriff auf die komplette Schatzkiste.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/#kontakt" data-testid="bibliothek-wunsch-cta">
                <Button className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[15px] font-semibold">
                  <span className="inline-flex items-center gap-2">
                    Leitfaden wünschen <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
              </Link>
              <Link to="/campus" data-testid="bibliothek-campus-cta">
                <Button variant="outline" className="rounded-full border-terra/40 bg-transparent text-terra hover:bg-peach-50 hover:text-terra h-12 px-7 text-[15px] font-semibold">
                  Dickies-Campus ansehen
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />

      {openGuide && (
        <BookReader
          guide={openGuide}
          slug={openSlug}
          onClose={() => setOpenSlug(null)}
        />
      )}
    </div>
  );
}
